import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, FileDown, Loader2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { cn } from "../../lib/utils";

/**
 * Letter grade derived from a 0-100 compliance score.
 * Same thresholds the backend uses for health scoring.
 */
function gradeFor(score) {
  if (score >= 90) return { letter: "A", tone: "text-green-600 dark:text-green-400" };
  if (score >= 80) return { letter: "B+", tone: "text-green-600 dark:text-green-400" };
  if (score >= 70) return { letter: "B", tone: "text-blue-600 dark:text-blue-400" };
  if (score >= 60) return { letter: "C", tone: "text-yellow-600 dark:text-yellow-400" };
  if (score >= 40) return { letter: "D", tone: "text-orange-600 dark:text-orange-400" };
  return { letter: "F", tone: "text-red-600 dark:text-red-400" };
}

function trendIcon(delta) {
  if (delta > 0) return <TrendingUp className="w-4 h-4" aria-hidden="true" />;
  if (delta < 0) return <TrendingDown className="w-4 h-4" aria-hidden="true" />;
  return <Minus className="w-4 h-4" aria-hidden="true" />;
}

function trendTone(delta) {
  if (delta > 0) return "text-green-600 dark:text-green-400";
  if (delta < 0) return "text-red-600 dark:text-red-400";
  return "text-muted-foreground";
}

export function HeroScore({
  greeting,
  score = 0,
  delta = 0,
  subtitle,
  onExport,
  exporting = false,
  exportLabel,
  isES = false,
}) {
  const { letter, tone } = gradeFor(score);
  const sign = delta > 0 ? "+" : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="overflow-hidden border-border bg-gradient-to-br from-primary/5 via-card to-card">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
            {/* Greeting + score numeric */}
            <div className="flex-1 min-w-0">
              {greeting && (
                <p className="text-sm text-muted-foreground mb-1">{greeting}</p>
              )}
              <h2 className="text-base font-medium text-muted-foreground mb-2">
                {isES ? "Salud de cumplimiento" : "Compliance Health"}
              </h2>
              <div className="flex items-end gap-4 mb-3">
                <div className="flex items-baseline gap-2">
                  <span className={cn("text-6xl md:text-7xl font-heading font-bold tracking-tight", tone)}>
                    {Math.round(score)}
                  </span>
                  <span className="text-2xl text-muted-foreground">/ 100</span>
                </div>
                <div className={cn("rounded-full border-2 border-current w-14 h-14 md:w-16 md:h-16 flex items-center justify-center font-heading font-bold text-2xl md:text-3xl", tone)}
                  aria-label={isES ? `Calificación ${letter}` : `Grade ${letter}`}>
                  {letter}
                </div>
              </div>
              <Progress
                value={score}
                className="h-2 mb-3"
                aria-label={isES ? "Progreso de cumplimiento" : "Compliance progress"}
              />
              <div className="flex items-center gap-3 text-sm">
                <span className={cn("inline-flex items-center gap-1 font-medium", trendTone(delta))}>
                  {trendIcon(delta)}
                  <span>
                    {delta === 0
                      ? (isES ? "Sin cambios" : "No change")
                      : `${sign}${delta} ${isES ? "este mes" : "this month"}`}
                  </span>
                </span>
                {subtitle && (
                  <>
                    <span className="text-border" aria-hidden="true">·</span>
                    <span className="text-muted-foreground">{subtitle}</span>
                  </>
                )}
              </div>
            </div>

            {/* Export action */}
            {onExport && (
              <div className="flex md:flex-col gap-2 md:items-end">
                <Button
                  size="lg"
                  variant="default"
                  onClick={onExport}
                  disabled={exporting}
                  data-testid="export-executive-pdf"
                >
                  {exporting
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                    : <FileDown className="w-4 h-4 mr-2" aria-hidden="true" />}
                  {exportLabel || (isES ? "Reporte Ejecutivo" : "Executive Report")}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
